import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import {
  getPreparedProduct,
  getPreparedProductRecipe,
  getPreparedProductRecipes,
  patchPreparedProduct,
  patchPreparedProductWithNewRecipe,
  patchPreparedProductWithRecipe,
} from "../api/preparedProductsApi";
import CreatePreparedProductPage from "./CreatePreparedProductPage";

const UNIT_LABELS = { g: "g", ml: "ml", unit: "unidad", package: "paquete" };

function unwrapHttp(response) {
  if (response && typeof response === "object" && "data" in response && !("meta" in response)) {
    return response.data;
  }
  return response;
}

function rowsFromResponse(response) {
  const data = unwrapHttp(response);
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  return Array.isArray(data) ? data : [];
}

function errorToMessage(error) {
  const message =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message;
  return Array.isArray(message) ? message.join(" | ") : message || "No se pudo cargar el elaborado.";
}

function hydrateComponents(recipe) {
  const ingredientLines = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  const preparedProductLines = Array.isArray(recipe?.preparedProducts)
    ? recipe.preparedProducts
    : [];

  const ingredients = ingredientLines.map((line) => {
    const ingredient = line.ingredient;
    return {
      kind: "ingredient",
      group: "Insumos",
      id: line.ingredientId,
      name: ingredient?.name || `Insumo #${line.ingredientId}`,
      unit: ingredient?.baseUnit || "",
      unitLabel: UNIT_LABELS[ingredient?.baseUnit] || ingredient?.baseUnit || "sin unidad",
      quantity: line.quantity,
      notes: line.notes || "",
      isAvailable: Boolean(ingredient?.isActive),
    };
  });
  const preparedProducts = preparedProductLines.map((line) => {
    const component = line.componentPreparedProduct;
    return {
      kind: "preparedProduct",
      group: "Elaborados",
      id: line.componentPreparedProductId,
      name: component?.name || `Elaborado #${line.componentPreparedProductId}`,
      unit: component?.resultUnit || "",
      unitLabel: UNIT_LABELS[component?.resultUnit] || component?.resultUnit || "sin unidad",
      quantity: line.quantity,
      notes: line.notes || "",
      isAvailable: Boolean(component?.isActive),
    };
  });

  return [...ingredients, ...preparedProducts];
}

export default function EditPreparedProductPage() {
  const navigate = useNavigate();
  const { preparedProductId } = useParams();
  const numericProductId = Number(preparedProductId);
  const [product, setProduct] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);

  const loadData = useCallback(async (signal) => {
    setLoading(true);
    setError("");
    try {
      const [productResponse, recipesResponse] = await Promise.all([
        getPreparedProduct(numericProductId, { signal }),
        getPreparedProductRecipes(numericProductId, { page: 1, limit: 1, signal }),
      ]);
      if (signal.aborted) return;

      const selectedRecipe = rowsFromResponse(recipesResponse)[0] || null;
      const loadedRecipe = selectedRecipe?.id
        ? unwrapHttp(await getPreparedProductRecipe(numericProductId, selectedRecipe.id, { signal }))
        : null;
      if (signal.aborted) return;
      const loadedComponents = loadedRecipe ? hydrateComponents(loadedRecipe) : [];

      setProduct(unwrapHttp(productResponse));
      setRecipe(loadedRecipe);
      setComponents(loadedComponents);
    } catch (requestError) {
      if (signal.aborted) return;
      setProduct(null);
      setRecipe(null);
      setComponents([]);
      setError(errorToMessage(requestError));
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [numericProductId]);

  useEffect(() => {
    if (!Number.isFinite(numericProductId) || numericProductId <= 0) return undefined;
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData, numericProductId, reloadVersion]);

  if (!Number.isFinite(numericProductId) || numericProductId <= 0) {
    return <Navigate to="/stock/base-de-datos/elaborados" replace />;
  }

  if (loading) {
    return (
      <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={30} />
        <Typography sx={{ mt: 1.5, fontWeight: 700, color: "neutral.700" }}>
          Cargando elaborado...
        </Typography>
      </Stack>
    );
  }

  if (error || !product) {
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <StockPageHeader title="Editar Elaborado" onBack={() => navigate("/stock/base-de-datos/elaborados")} />
        <Box sx={{ p: 3, borderRadius: "22px", bgcolor: "neutral.300" }}>
          <Alert severity="error">{error || "No se pudo cargar el elaborado."}</Alert>
          <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: "flex-end" }}>
            <Button onClick={() => navigate("/stock/base-de-datos/elaborados")}>Volver</Button>
            <Button variant="contained" onClick={() => setReloadVersion((value) => value + 1)}>
              Reintentar
            </Button>
          </Stack>
        </Box>
      </Stack>
    );
  }

  const initialValues = {
    name: product.name,
    sector: product.sector,
    yieldPercent:
      product.yieldFactor === null || product.yieldFactor === undefined
        ? ""
        : Number(product.yieldFactor) * 100,
    resultQuantity: recipe?.resultQuantity ?? "",
    recipeQuantity: recipe?.recipeQuantity ?? "",
    resultUnit: product.resultUnit,
    isActive: Boolean(product.isActive),
    recipeIsActive: Boolean(recipe?.isActive),
    hasRecipe: Boolean(recipe),
    notes: recipe?.notes || "",
    components,
  };

  async function handleSave(payload) {
    const productPayload = {
      name: payload.name,
      sector: payload.sector,
      resultUnit: payload.resultUnit,
      yieldFactor: payload.yieldFactor,
      isActive: payload.isActive,
    };
    if (!payload.recipe) {
      await patchPreparedProduct(numericProductId, productPayload);
      return;
    }
    if (!recipe) {
      await patchPreparedProductWithNewRecipe(numericProductId, payload);
      return;
    }
    await patchPreparedProductWithRecipe(numericProductId, recipe.id, {
      product: {
        ...productPayload,
      },
      recipe: payload.recipe,
    });
  }

  return (
    <CreatePreparedProductPage
      initialValues={initialValues}
      pageTitle="Editar Elaborado"
      showStatus
      successMessage="El elaborado se actualizó correctamente."
      onSave={handleSave}
      excludedPreparedProductId={numericProductId}
      allowInactiveWithoutRecipe={!recipe}
    />
  );
}
